import type {
  ApiError,
  ClientContext,
  CompleteResponse,
  ConsentResponse,
  FieldValue,
  InputsResponse,
  SessionResponse,
  StatusResponse,
} from './types';

// One fixed API origin serves every tenant subdomain — the token in the path
// is the only thing that varies. See PLAN.md §9.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://verify-api.inmutable.app';

export class ApiRequestError extends Error {
  status: number;
  code?: string;
  // Per-field messages (422 from POST /inputs).
  fieldErrors?: Record<string, string>;

  constructor(status: number, body: ApiError | null) {
    super(body?.message || body?.error || `Request failed (${status})`);
    this.status = status;
    this.code = body?.error;
    if (body?.errors && typeof body.errors === 'object') this.fieldErrors = body.errors;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Content-Type only when there's a body: on a GET it isn't needed, and it
  // turns every poll into a CORS-preflighted request (two round trips).
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      ...(init?.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers || {}),
    },
  });

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // empty body (e.g. 204) — fine
  }

  if (!res.ok) {
    throw new ApiRequestError(res.status, body as ApiError | null);
  }
  return body as T;
}

export function getStatus(token: string): Promise<StatusResponse> {
  return request<StatusResponse>(`/public/v/${encodeURIComponent(token)}`, { method: 'GET' });
}

export function postConsent(
  token: string,
  platformConsentVersion?: string,
  client?: ClientContext,
): Promise<ConsentResponse> {
  return request<ConsentResponse>(`/public/v/${encodeURIComponent(token)}/consent`, {
    method: 'POST',
    body: JSON.stringify({ platformConsentVersion, client }),
  });
}

export function postSession(token: string, client?: ClientContext): Promise<SessionResponse> {
  return request<SessionResponse>(`/public/v/${encodeURIComponent(token)}/session`, {
    method: 'POST',
    body: JSON.stringify({ client }),
  });
}

export function postComplete(token: string, sessionId: string, client?: ClientContext): Promise<CompleteResponse> {
  return request<CompleteResponse>(`/public/v/${encodeURIComponent(token)}/complete`, {
    method: 'POST',
    body: JSON.stringify({ sessionId, client }),
  });
}

export function postAbort(token: string, errorState: string, client?: ClientContext): Promise<{ status: string }> {
  return request<{ status: string }>(`/public/v/${encodeURIComponent(token)}/abort`, {
    method: 'POST',
    body: JSON.stringify({ errorState, client }),
  });
}

export type PostInputsResult =
  // 202: accepted; keep polling GET until step === 'done'.
  | { kind: 'accepted'; step: InputsResponse['step'] }
  // 422: show each message next to its field.
  | { kind: 'invalid'; errors: Record<string, string> }
  // 409: nothing pending (already answered, or the deadline passed) — same as accepted for the person.
  | { kind: 'nothingPending' };

export async function postInputs(token: string, values: Record<string, FieldValue>): Promise<PostInputsResult> {
  try {
    const resp = await request<InputsResponse>(`/public/v/${encodeURIComponent(token)}/inputs`, {
      method: 'POST',
      body: JSON.stringify({ values }),
    });
    return { kind: 'accepted', step: resp?.step === 'done' ? 'done' : 'processing' };
  } catch (e: unknown) {
    if (e instanceof ApiRequestError && e.status === 422) {
      return { kind: 'invalid', errors: e.fieldErrors ?? {} };
    }
    if (e instanceof ApiRequestError && e.status === 409) {
      return { kind: 'nothingPending' };
    }
    throw e;
  }
}
